import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    // Intentionally swallowed to avoid crashing the app again while still
    // allowing React to surface the fallback UI.
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <main
        className="min-h-screen bg-background text-foreground flex items-center justify-center px-6"
        style={{ fontFamily: "var(--font-body)" }}
      >
        <section
          className="w-full max-w-[560px] bg-card border border-border rounded-2xl p-8 text-center shadow-2xl"
          style={{ boxShadow: "var(--shadow-modal)" }}
        >
          <p
            className="text-primary mb-3"
            style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.08em" }}
          >
            SYSTEM RECOVERY
          </p>
          <h1
            className="text-foreground mb-3"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 28 }}
          >
            Something went off-script
          </h1>
          <p
            className="text-muted-foreground mb-8"
            style={{ fontFamily: "var(--font-body)", fontSize: 15, lineHeight: 1.6 }}
          >
            The page hit an unexpected error. You can reload the experience or head back to the main dashboard.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="h-11 px-6 rounded-lg bg-primary text-primary-foreground hover:bg-primary-hover transition-colors"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}
            >
              Reload page
            </button>
            <a
              href="/"
              className="h-11 px-6 rounded-lg border border-border text-foreground hover:border-primary hover:text-primary transition-colors inline-flex items-center justify-center"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}
            >
              Go home
            </a>
          </div>
        </section>
      </main>
    );
  }
}
